import type { Practice, SocialNetwork } from "@brandex/api-client";
import type { MessageKey } from "@brandex/i18n";
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, ChoiceChips, Field, Input } from "@brandex/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Award, BookOpen, Briefcase, ChevronDown, HeartPulse, Link2, Plus, Stethoscope, Trash2, Users, Video, X } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useBrandex, useT } from "../context";
import { errorMessage } from "../errors";
import { keys } from "../queries";
import {
  emphasisOk,
  LIMITS,
  NETWORKS,
  PUBLICATION_KINDS,
  showcaseToDraft,
  toShowcaseRequest,
  validateShowcase,
  yearsInPractice,
  type ShowcaseDraft,
  type ShowcaseErrors,
} from "./showcase-draft";

type Patch = (fn: (d: ShowcaseDraft) => ShowcaseDraft) => void;

/**
 * Portfolio highlights (F-06): what turns the website from a business card
 * into a personal brand. One form, saved with one request.
 */
export function ShowcaseCard({ orgId, practice }: { orgId: string; practice: Practice }) {
  const t = useT();
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<ShowcaseDraft>(() => showcaseToDraft(practice.profile.showcase));
  const [errors, setErrors] = useState<ShowcaseErrors>({});
  const save = useMutation({
    mutationFn: async (d: ShowcaseDraft) => {
      const res = await api.http.PUT("/v1/orgs/{org_id}/practice/showcase", {
        params: { path: { org_id: orgId } },
        body: toShowcaseRequest(d),
      });
      // The client throws typed errors for failed requests.
      if (res.data === undefined) throw new Error("Empty response");
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(keys.practice(orgId), data);
      setDraft(showcaseToDraft(data.profile.showcase));
    },
  });
  const patch: Patch = (fn) => {
    save.reset();
    setDraft(fn);
  };
  const err = (key: string) => (errors[key] ? t(errors[key]) : undefined);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("showcase.title")}</CardTitle>
        <CardDescription>{t("showcase.hint")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          noValidate
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            const v = validateShowcase(draft, new Date().getFullYear());
            setErrors(v);
            if (Object.keys(v).length === 0) save.mutate(draft);
          }}
        >
          <Field label={t("showcase.tagline")} hint={t("showcase.taglineHint", { count: draft.tagline.trim().length, max: LIMITS.tagline })} error={err("tagline")}>
            <Input
              value={draft.tagline}
              maxLength={LIMITS.tagline}
              placeholder={t("showcase.taglinePlaceholder")}
              onChange={(e) => patch((d) => ({ ...d, tagline: e.target.value }))}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-[1fr_15rem]">
            <Field
              label={t("showcase.headline")}
              hint={t("showcase.headlineHint", { count: draft.headline.trim().length, max: LIMITS.headline })}
              error={err("headline")}
            >
              <Input
                value={draft.headline}
                maxLength={LIMITS.headline}
                placeholder={t("showcase.headlinePlaceholder")}
                onChange={(e) => patch((d) => ({ ...d, headline: e.target.value }))}
              />
            </Field>
            <Field
              label={t("showcase.since")}
              hint={experienceYears(draft.since) === null ? t("showcase.sinceHint") : t("showcase.sinceYears", { n: experienceYears(draft.since) ?? 0 })}
              error={err("since")}
            >
              <Input
                inputMode="numeric"
                maxLength={4}
                placeholder="2010"
                value={draft.since}
                onChange={(e) => patch((d) => ({ ...d, since: e.target.value.replace(/\D/g, "") }))}
              />
            </Field>
          </div>
          {draft.headline.includes("*") && emphasisOk(draft.headline) ? (
            <p className="-mt-2 text-sm text-muted-foreground">
              {t("showcase.emphasisPreview")} <Emphasis text={draft.headline} />
            </p>
          ) : null}

          <Group icon={<HeartPulse className="h-4 w-4" />} title={t("showcase.careAreas")} hint={t("showcase.careAreasHint")} count={draft.careAreas.length}>
            <Rows
              items={draft.careAreas}
              max={LIMITS.careAreas}
              addLabel={t("showcase.addCareArea")}
              removeLabel={(n) => t("showcase.removeCareArea", { n })}
              onAdd={() => patch((d) => ({ ...d, careAreas: [...d.careAreas, { title: "", description: "" }] }))}
              onRemove={(i) => patch((d) => ({ ...d, careAreas: d.careAreas.filter((_, j) => j !== i) }))}
              columns=""
              render={(a, i) => {
                const set = (x: Partial<ShowcaseDraft["careAreas"][number]>) =>
                  patch((d) => ({ ...d, careAreas: d.careAreas.map((y, j) => (j === i ? { ...y, ...x } : y)) }));
                return (
                  <>
                    <Field label={t("showcase.careTitle", { n: i + 1 })} error={err(`careAreas.${i}.title`)}>
                      <Input value={a.title} maxLength={LIMITS.careTitle} placeholder={t("showcase.careTitlePlaceholder")} onChange={(e) => set({ title: e.target.value })} />
                    </Field>
                    <Field
                      label={t("showcase.careDescription")}
                      hint={t("showcase.careDescriptionHint", { count: a.description.trim().length, max: LIMITS.careDescription })}
                      error={err(`careAreas.${i}.description`)}
                    >
                      <textarea
                        className="min-h-20 w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground"
                        value={a.description}
                        maxLength={LIMITS.careDescription}
                        placeholder={t("showcase.careDescriptionPlaceholder")}
                        onChange={(e) => set({ description: e.target.value })}
                      />
                    </Field>
                  </>
                );
              }}
            />
          </Group>

          <Group icon={<Video className="h-4 w-4" />} title={t("showcase.online")} hint={t("showcase.onlineHint")} count={draft.online.offered ? 1 : 0}>
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1"
                checked={draft.online.offered}
                onChange={(e) => patch((d) => ({ ...d, online: { ...d.online, offered: e.target.checked } }))}
              />
              <span className="font-medium">{t("showcase.onlineOffered")}</span>
            </label>
            {draft.online.offered ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t("showcase.onlineNote")} hint={t("showcase.onlineNoteHint")} error={err("online.note")}>
                  <Input value={draft.online.note} maxLength={LIMITS.onlineNote} placeholder={t("showcase.onlineNotePlaceholder")} onChange={(e) => patch((d) => ({ ...d, online: { ...d.online, note: e.target.value } }))} />
                </Field>
                <Field label={t("showcase.onlineUrl")} hint={t("showcase.onlineUrlHint")} error={err("online.url")}>
                  <Input type="url" inputMode="url" placeholder="https://" maxLength={500} value={draft.online.url} onChange={(e) => patch((d) => ({ ...d, online: { ...d.online, url: e.target.value } }))} />
                </Field>
              </div>
            ) : null}
          </Group>

          <Group icon={<Stethoscope className="h-4 w-4" />} title={t("showcase.services")} hint={t("showcase.servicesHint")} count={draft.services.length} open>
            <Services draft={draft} patch={patch} errors={errors} specialty={practice.profile.specialty} />
          </Group>

          <Group icon={<Briefcase className="h-4 w-4" />} title={t("showcase.experience")} hint={t("showcase.experienceHint")} count={draft.experience.length}>
            <ExperienceList draft={draft} patch={patch} err={err} />
          </Group>

          <Group icon={<Award className="h-4 w-4" />} title={t("showcase.awards")} hint={t("showcase.awardsHint")} count={draft.awards.length}>
            <Rows
              items={draft.awards}
              max={LIMITS.awards}
              addLabel={t("showcase.addAward")}
              removeLabel={(n) => t("showcase.removeAward", { n })}
              onAdd={() => patch((d) => ({ ...d, awards: [...d.awards, { title: "", issuer: "", year: "" }] }))}
              onRemove={(i) => patch((d) => ({ ...d, awards: d.awards.filter((_, j) => j !== i) }))}
              columns="sm:grid-cols-[1.5fr_1.2fr_6rem_auto]"
              render={(a, i) => (
                <>
                  <Field label={t("showcase.awardTitle")} error={err(`awards.${i}.title`)}>
                    <Input value={a.title} maxLength={160} onChange={(e) => patch((d) => ({ ...d, awards: d.awards.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) }))} />
                  </Field>
                  <Field label={t("showcase.awardIssuer")}>
                    <Input value={a.issuer} maxLength={160} onChange={(e) => patch((d) => ({ ...d, awards: d.awards.map((x, j) => (j === i ? { ...x, issuer: e.target.value } : x)) }))} />
                  </Field>
                  <Field label={t("showcase.year")} error={err(`awards.${i}.year`)}>
                    <Input inputMode="numeric" maxLength={4} value={a.year} onChange={(e) => patch((d) => ({ ...d, awards: d.awards.map((x, j) => (j === i ? { ...x, year: e.target.value.replace(/\D/g, "") } : x)) }))} />
                  </Field>
                </>
              )}
            />
          </Group>

          <Group icon={<Users className="h-4 w-4" />} title={t("showcase.memberships")} hint={t("showcase.membershipsHint")} count={draft.memberships.length}>
            <Rows
              items={draft.memberships}
              max={LIMITS.memberships}
              addLabel={t("showcase.addMembership")}
              removeLabel={(n) => t("showcase.removeMembership", { n })}
              onAdd={() => patch((d) => ({ ...d, memberships: [...d.memberships, { organization: "", role: "" }] }))}
              onRemove={(i) => patch((d) => ({ ...d, memberships: d.memberships.filter((_, j) => j !== i) }))}
              columns="sm:grid-cols-[2fr_1fr_auto]"
              render={(m, i) => (
                <>
                  <Field label={t("showcase.membershipOrg")} error={err(`memberships.${i}.organization`)}>
                    <Input value={m.organization} maxLength={160} placeholder={t("showcase.membershipPlaceholder")} onChange={(e) => patch((d) => ({ ...d, memberships: d.memberships.map((x, j) => (j === i ? { ...x, organization: e.target.value } : x)) }))} />
                  </Field>
                  <Field label={t("showcase.membershipRole")}>
                    <Input value={m.role} maxLength={80} placeholder={t("showcase.membershipRolePlaceholder")} onChange={(e) => patch((d) => ({ ...d, memberships: d.memberships.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)) }))} />
                  </Field>
                </>
              )}
            />
          </Group>

          <Group icon={<BookOpen className="h-4 w-4" />} title={t("showcase.publications")} hint={t("showcase.publicationsHint")} count={draft.publications.length}>
            <Rows
              items={draft.publications}
              max={LIMITS.publications}
              addLabel={t("showcase.addPublication")}
              removeLabel={(n) => t("showcase.removePublication", { n })}
              onAdd={() => patch((d) => ({ ...d, publications: [...d.publications, { kind: "journal", title: "", venue: "", year: "", url: "" }] }))}
              onRemove={(i) => patch((d) => ({ ...d, publications: d.publications.filter((_, j) => j !== i) }))}
              columns="sm:grid-cols-2"
              render={(p, i) => {
                const set = (x: Partial<ShowcaseDraft["publications"][number]>) =>
                  patch((d) => ({ ...d, publications: d.publications.map((y, j) => (j === i ? { ...y, ...x } : y)) }));
                return (
                  <>
                    <div className="sm:col-span-2">
                      <ChoiceChips
                        label={t("showcase.publicationKind")}
                        value={p.kind}
                        onChange={(kind) => set({ kind })}
                        options={PUBLICATION_KINDS.map((k) => ({ value: k, label: t(`showcase.kind.${k}` as MessageKey) }))}
                      />
                    </div>
                    <Field label={t("showcase.publicationTitle")} error={err(`publications.${i}.title`)} className="sm:col-span-2">
                      <Input value={p.title} maxLength={200} onChange={(e) => set({ title: e.target.value })} />
                    </Field>
                    <Field label={t(`showcase.venue.${p.kind}` as MessageKey)}>
                      <Input value={p.venue} maxLength={160} onChange={(e) => set({ venue: e.target.value })} />
                    </Field>
                    <Field label={t("showcase.year")} error={err(`publications.${i}.year`)}>
                      <Input inputMode="numeric" maxLength={4} value={p.year} onChange={(e) => set({ year: e.target.value.replace(/\D/g, "") })} />
                    </Field>
                    <Field label={t("showcase.publicationUrl")} error={err(`publications.${i}.url`)} className="sm:col-span-2">
                      <Input type="url" inputMode="url" placeholder="https://" value={p.url} maxLength={500} onChange={(e) => set({ url: e.target.value })} />
                    </Field>
                  </>
                );
              }}
            />
          </Group>

          <Group icon={<Link2 className="h-4 w-4" />} title={t("showcase.links")} hint={t("showcase.linksHint")} count={NETWORKS.filter((n) => draft.links[n].trim() !== "").length}>
            <div className="grid gap-3 sm:grid-cols-2">
              {NETWORKS.map((n) => (
                <Field key={n} label={t(`showcase.network.${n}` as MessageKey)} error={err(`links.${n}`)}>
                  <Input
                    type="url"
                    inputMode="url"
                    maxLength={500}
                    placeholder={PLACEHOLDER[n]}
                    value={draft.links[n]}
                    onChange={(e) => patch((d) => ({ ...d, links: { ...d.links, [n]: e.target.value } }))}
                  />
                </Field>
              ))}
            </div>
          </Group>

          {Object.keys(errors).length > 0 ? <Alert tone="error">{t("showcase.fixErrors")}</Alert> : null}
          {save.isError ? <Alert tone="error">{errorMessage(save.error, t)}</Alert> : null}
          {save.isSuccess ? <Alert tone="success">{t("profile.saved")}</Alert> : null}
          <div>
            <Button type="submit" loading={save.isPending}>{t("showcase.save")}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/** Whole years of experience from a typed start year, as the website shows them; null until it is a real year. */
export function experienceYears(since: string, now = new Date()): number | null {
  const year = /^\d{4}$/.test(since.trim()) ? Number(since.trim()) : null;
  if (year === null || year < 1950 || year > now.getFullYear()) return null;
  return yearsInPractice(year, now);
}

/** A headline with its `*emphasis*` shown in italics, as the website shows it. */
function Emphasis({ text }: { text: string }) {
  return (
    <span className="text-foreground">
      {text.split("*").map((part, i) => (i % 2 === 1 ? <em key={i} className="font-serif">{part}</em> : <span key={i}>{part}</span>))}
    </span>
  );
}

const PLACEHOLDER: Record<SocialNetwork, string> = {
  facebook: "https://facebook.com/…",
  youtube: "https://youtube.com/@…",
  instagram: "https://instagram.com/…",
  linkedin: "https://linkedin.com/in/…",
  x: "https://x.com/…",
  tiktok: "https://tiktok.com/@…",
  website: "https://…",
};

/** A collapsible part of the form (native `<details>`: keyboard and screen-reader friendly). */
function Group({ icon, title, hint, count, open = false, children }: { icon: ReactNode; title: string; hint: string; count: number; open?: boolean; children: ReactNode }) {
  return (
    <details open={open || undefined} className="group rounded-[var(--radius-lg)] border border-border bg-card/40 [&_summary::-webkit-details-marker]:hidden">
      <summary className="flex cursor-pointer list-none items-center gap-3 rounded-[var(--radius-lg)] px-4 py-3 hover:bg-muted/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
        <span aria-hidden className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">{icon}</span>
        <span className="grid min-w-0 flex-1">
          <span className="font-medium">{title}</span>
          <span className="truncate text-xs text-muted-foreground">{hint}</span>
        </span>
        {count > 0 ? <Badge tone="neutral">{count}</Badge> : null}
        <ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180 motion-reduce:transition-none" />
      </summary>
      <div className="grid gap-3 border-t border-border p-4">{children}</div>
    </details>
  );
}

function Rows<T>({
  items,
  max,
  addLabel,
  removeLabel,
  onAdd,
  onRemove,
  columns,
  render,
}: {
  items: T[];
  max: number;
  addLabel: string;
  removeLabel: (n: number) => string;
  onAdd: () => void;
  onRemove: (i: number) => void;
  columns: string;
  render: (item: T, i: number) => ReactNode;
}) {
  return (
    <>
      {items.map((item, i) => (
        <div key={i} className="relative rounded-[var(--radius-lg)] border border-border p-3 pr-12">
          <div className={`grid gap-3 sm:items-end ${columns}`}>{render(item, i)}</div>
          <Button type="button" variant="ghost" size="icon" className="absolute right-1.5 top-1.5" aria-label={removeLabel(i + 1)} onClick={() => onRemove(i)}>
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ))}
      {items.length < max ? (
        <div>
          <Button type="button" variant="outline" size="sm" onClick={onAdd}>
            <Plus className="h-4 w-4" aria-hidden />
            {addLabel}
          </Button>
        </div>
      ) : null}
    </>
  );
}

function ExperienceList({ draft, patch, err }: { draft: ShowcaseDraft; patch: Patch; err: (k: string) => string | undefined }) {
  const t = useT();
  return (
    <Rows
      items={draft.experience}
      max={LIMITS.experience}
      addLabel={t("showcase.addExperience")}
      removeLabel={(n) => t("showcase.removeExperience", { n })}
      onAdd={() => patch((d) => ({ ...d, experience: [...d.experience, { role: "", organization: "", from: "", to: "", current: d.experience.length === 0 }] }))}
      onRemove={(i) => patch((d) => ({ ...d, experience: d.experience.filter((_, j) => j !== i) }))}
      columns="sm:grid-cols-2"
      render={(e, i) => {
        const set = (x: Partial<ShowcaseDraft["experience"][number]>) =>
          patch((d) => ({ ...d, experience: d.experience.map((y, j) => (j === i ? { ...y, ...x } : y)) }));
        return (
          <>
            <Field label={t("showcase.role")} error={err(`experience.${i}.role`)}>
              <Input value={e.role} maxLength={120} placeholder={t("showcase.rolePlaceholder")} onChange={(ev) => set({ role: ev.target.value })} />
            </Field>
            <Field label={t("showcase.organization")} error={err(`experience.${i}.organization`)}>
              <Input value={e.organization} maxLength={160} onChange={(ev) => set({ organization: ev.target.value })} />
            </Field>
            <Field label={t("showcase.from")} error={err(`experience.${i}.from`)}>
              <Input inputMode="numeric" maxLength={4} value={e.from} onChange={(ev) => set({ from: ev.target.value.replace(/\D/g, "") })} />
            </Field>
            <Field label={t("showcase.to")} error={err(`experience.${i}.to`)}>
              <Input
                inputMode="numeric"
                maxLength={4}
                disabled={e.current}
                placeholder={e.current ? t("showcase.present") : ""}
                value={e.current ? "" : e.to}
                onChange={(ev) => set({ to: ev.target.value.replace(/\D/g, "") })}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm sm:col-span-full">
              <input type="checkbox" className="h-4 w-4 accent-[var(--primary)]" checked={e.current} onChange={(ev) => set({ current: ev.target.checked })} />
              {t("showcase.current")}
            </label>
          </>
        );
      }}
    />
  );
}

/** Services as removable chips, typed in one at a time, with specialty suggestions. */
function Services({ draft, patch, errors, specialty }: { draft: ShowcaseDraft; patch: Patch; errors: ShowcaseErrors; specialty: Practice["profile"]["specialty"] }) {
  const t = useT();
  const [text, setText] = useState("");
  const has = (s: string) => draft.services.some((x) => x.trim().toLowerCase() === s.trim().toLowerCase());
  const full = draft.services.length >= LIMITS.services;
  const add = (raw: string) => {
    const s = raw.trim().replace(/\s+/g, " ");
    if (s.length < 2 || has(s) || full) return;
    patch((d) => ({ ...d, services: [...d.services, s.slice(0, LIMITS.service)] }));
  };
  const suggestions = t(`showcase.suggest.${specialty}` as MessageKey)
    .split("|")
    .map((s) => s.trim())
    .filter((s) => s && !has(s));
  const firstError = Object.entries(errors).find(([k]) => k.startsWith("services."));
  return (
    <div className="grid gap-3">
      {draft.services.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label={t("showcase.services")}>
          {draft.services.map((s, i) => (
            <li
              key={`${s}-${i}`}
              className={`inline-flex items-center gap-1 rounded-[var(--radius-pill)] border py-1 pl-3 pr-1 text-sm ${errors[`services.${i}`] ? "border-destructive" : "border-border bg-muted/50"}`}
            >
              {s}
              <button
                type="button"
                aria-label={t("showcase.removeService", { name: s })}
                className="grid h-6 w-6 place-items-center rounded-full hover:bg-background focus-visible:outline-2 focus-visible:outline-ring"
                onClick={() => patch((d) => ({ ...d, services: d.services.filter((_, j) => j !== i) }))}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex gap-2">
        <Field label={t("showcase.addService")} className="flex-1" hint={t("showcase.serviceCount", { count: draft.services.length, max: LIMITS.services })} error={firstError ? t(firstError[1]) : undefined}>
          <Input
            value={text}
            maxLength={LIMITS.service}
            disabled={full}
            placeholder={t("showcase.servicePlaceholder")}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                add(text);
                setText("");
              }
            }}
          />
        </Field>
        <Button
          type="button"
          variant="outline"
          className="mt-6"
          disabled={full || text.trim().length < 2}
          onClick={() => {
            add(text);
            setText("");
          }}
        >
          <Plus className="h-4 w-4" aria-hidden />
          {t("showcase.add")}
        </Button>
      </div>
      {suggestions.length > 0 && !full ? (
        <div className="grid gap-2">
          <p className="text-xs font-medium text-muted-foreground">{t("showcase.suggestions")}</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => add(s)}
                className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] border border-dashed border-border px-3 py-1 text-sm text-muted-foreground hover:border-primary hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                {s}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
